import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CheckSquare,
  Eye,
  FileText,
  History,
  Key,
  LogOut,
  Network,
  Save,
  Scissors,
  Search,
  Shield,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { ApiKeysDialog } from "./components/ApiKeysDialog";
import { AttachmentsPanel } from "./components/AttachmentsPanel";
import { BacklinksPanel } from "./components/BacklinksPanel";
import { ChatPanel } from "./components/ChatPanel";
import { ClipDialog } from "./components/ClipDialog";
import { FolderSelect } from "./components/FolderSelect";
import { FolderTree } from "./components/FolderTree";
import { GraphView } from "./components/GraphView";
import { KanbanView } from "./components/KanbanView";
import { LoginForm } from "./components/LoginForm";
import { MarkdownPreview } from "./components/MarkdownPreview";
import { NoteList } from "./components/NoteList";
import { NotePreviewDialog } from "./components/NotePreviewDialog";
import { RevisionsPanel } from "./components/RevisionsPanel";
import { SearchPalette } from "./components/SearchPalette";
import { TagChips } from "./components/TagChips";
import { VaultPanel } from "./components/VaultPanel";
import { NoteEditor } from "./components/editor/NoteEditor";
import { ApiError, api } from "./lib/api";
import { getToken, logout } from "./lib/auth";
import type { NoteDetail } from "./lib/types";

type View = "editor" | "graph" | "kanban";

export default function App() {
  const [authed, setAuthed] = useState<boolean>(() => Boolean(getToken()));
  if (!authed) {
    return <LoginForm onSuccess={() => setAuthed(true)} />;
  }
  return <AuthedApp onLogout={() => setAuthed(false)} />;
}

function AuthedApp({ onLogout }: { onLogout: () => void }) {
  const qc = useQueryClient();
  const [view, setView] = useState<View>("editor");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [clipOpen, setClipOpen] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"edit" | "preview">("edit");
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [previewNoteId, setPreviewNoteId] = useState<string | null>(null);

  // Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const notesQ = useQuery({
    queryKey: ["notes", "", folderId ?? "all"],
    queryFn: () => api.listNotes({ limit: 100, folder_id: folderId }),
  });
  useEffect(() => {
    if (!selectedId && notesQ.data && notesQ.data.length > 0) {
      setSelectedId(notesQ.data[0].id);
    }
  }, [notesQ.data, selectedId]);

  const noteQ = useQuery({
    queryKey: ["note", selectedId],
    queryFn: () => api.getNote(selectedId!),
    enabled: !!selectedId,
  });

  const saveMut = useMutation({
    mutationFn: async (body: {
      id: string;
      version: number;
      content_md?: string;
      tags?: string[];
      title?: string;
      folder_id?: string | null;
    }) => api.updateNote(body.id, body, body.version),
    onSuccess: (updated) => {
      qc.setQueryData<NoteDetail>(["note", updated.id], updated);
      qc.invalidateQueries({ queryKey: ["notes"] });
      qc.invalidateQueries({ queryKey: ["backlinks"] });
      qc.invalidateQueries({ queryKey: ["outgoing"] });
      qc.invalidateQueries({ queryKey: ["graph"] });
      setStatusMsg(`Gespeichert · v${updated.version}`);
      setTimeout(() => setStatusMsg(null), 2000);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 412) {
        setStatusMsg("Konflikt: Note wurde anderswo geändert");
      } else {
        setStatusMsg(`Fehler: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  });

  const createMut = useMutation({
    mutationFn: (title: string) => api.createNote({ title, content_md: "" }),
    onSuccess: (note) => {
      qc.invalidateQueries({ queryKey: ["notes"] });
      qc.invalidateQueries({ queryKey: ["graph"] });
      setSelectedId(note.id);
      setView("editor");
    },
  });

  const createFolderMut = useMutation({
    mutationFn: ({ name, parent_id }: { name: string; parent_id: string | null }) =>
      api.createFolder({ name, parent_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders"] }),
  });

  const handleSaveContent = useCallback(
    (content_md: string) => {
      if (!noteQ.data) return;
      saveMut.mutate({ id: noteQ.data.id, content_md, version: noteQ.data.version });
    },
    [noteQ.data, saveMut],
  );

  const handleSaveTags = useCallback(
    (tags: string[]) => {
      if (!noteQ.data) return;
      saveMut.mutate({ id: noteQ.data.id, tags, version: noteQ.data.version });
    },
    [noteQ.data, saveMut],
  );

  const handleCreate = () => {
    const title = window.prompt("Titel der neuen Note?");
    if (!title) return;
    createMut.mutate(title);
  };

  const handleCreateFolder = (parent_id: string | null) => {
    const name = window.prompt("Name des Ordners?");
    if (!name) return;
    createFolderMut.mutate({ name, parent_id });
  };

  const handleNavigateWikilink = async (target: string) => {
    const matches = await api.listNotes({ q: target, limit: 10 });
    const exact = matches.find((n) => n.title.toLowerCase() === target.toLowerCase());
    if (exact) {
      setSelectedId(exact.id);
      setView("editor");
      return;
    }
    if (window.confirm(`Note "${target}" existiert nicht. Anlegen?`)) {
      createMut.mutate(target);
    }
  };

  const handleOpenToday = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const note = await api.getOrCreateDaily(today);
    qc.invalidateQueries({ queryKey: ["notes"] });
    setSelectedId(note.id);
    setView("editor");
  };

  async function handleLogout() {
    await logout();
    onLogout();
  }

  return (
    <div className="flex h-screen flex-col bg-zinc-950 text-zinc-100">
      <header className="flex items-center justify-between border-b border-zinc-800 bg-zinc-950/80 px-4 py-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">ThunderBrain</span>
          {view === "editor" && noteQ.data && (
            <span className="text-xs text-zinc-400">
              {noteQ.data.title} · v{noteQ.data.version}
            </span>
          )}
          {view === "graph" && <span className="text-xs text-zinc-400">Graph</span>}
          {view === "kanban" && <span className="text-xs text-zinc-400">Kanban / PARA</span>}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {statusMsg && <span className="mr-2 text-emerald-400">{statusMsg}</span>}
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
            title="Cmd/Ctrl+K"
          >
            <Search className="h-3 w-3" />
            Suche
            <kbd className="ml-1 rounded bg-zinc-800 px-1 py-0.5 text-[9px] text-zinc-400">⌘K</kbd>
          </button>
          <button
            onClick={handleOpenToday}
            className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
          >
            <CalendarDays className="h-3 w-3" />
            Heute
          </button>
          <button
            onClick={() => setClipOpen(true)}
            className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
          >
            <Scissors className="h-3 w-3" />
            Clip
          </button>
          <button
            onClick={() => setChatOpen((o) => !o)}
            className={`flex items-center gap-1 rounded border px-2 py-1 ${
              chatOpen
                ? "border-emerald-500 text-emerald-400"
                : "border-zinc-700 hover:border-emerald-500 hover:text-emerald-400"
            }`}
          >
            <Sparkles className="h-3 w-3" />
            Chat
          </button>
          <button
            onClick={() => setView(view === "kanban" ? "editor" : "kanban")}
            className={`flex items-center gap-1 rounded border px-2 py-1 ${
              view === "kanban"
                ? "border-emerald-500 text-emerald-400"
                : "border-zinc-700 hover:border-emerald-500 hover:text-emerald-400"
            }`}
          >
            <CheckSquare className="h-3 w-3" />
            Tasks
          </button>
          <button
            onClick={() => setView(view === "graph" ? "editor" : "graph")}
            className={`flex items-center gap-1 rounded border px-2 py-1 ${
              view === "graph"
                ? "border-emerald-500 text-emerald-400"
                : "border-zinc-700 hover:border-emerald-500 hover:text-emerald-400"
            }`}
          >
            <Network className="h-3 w-3" />
            Graph
          </button>
          <button
            onClick={() => setVaultOpen((o) => !o)}
            className={`flex items-center gap-1 rounded border px-2 py-1 ${
              vaultOpen
                ? "border-emerald-500 text-emerald-400"
                : "border-zinc-700 hover:border-emerald-500 hover:text-emerald-400"
            }`}
            title="Passwort-Vault (verschlüsselt)"
          >
            <Shield className="h-3 w-3" />
            Vault
          </button>
          <button
            onClick={() => setKeysOpen(true)}
            className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
            title="API-Keys für Sibling-Apps"
          >
            <Key className="h-3 w-3" />
            Keys
          </button>
          {view === "editor" && noteQ.data && (
            <>
              <button
                onClick={() => setEditorMode(editorMode === "edit" ? "preview" : "edit")}
                className={`flex items-center gap-1 rounded border px-2 py-1 ${
                  editorMode === "preview"
                    ? "border-emerald-500 text-emerald-400"
                    : "border-zinc-700 hover:border-emerald-500 hover:text-emerald-400"
                }`}
                title={editorMode === "edit" ? "Preview anzeigen" : "Zurück zum Editor"}
              >
                {editorMode === "edit" ? (
                  <Eye className="h-3 w-3" />
                ) : (
                  <FileText className="h-3 w-3" />
                )}
                {editorMode === "edit" ? "Preview" : "Edit"}
              </button>
              <button
                onClick={() => setRevisionsOpen(true)}
                className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400"
              >
                <History className="h-3 w-3" />
                History
              </button>
              <button
                onClick={() => handleSaveContent(noteQ.data!.content_md)}
                disabled={saveMut.isPending}
                className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-emerald-500 hover:text-emerald-400 disabled:opacity-40"
                title="Cmd/Ctrl+S"
              >
                <Save className="h-3 w-3" />
                Save
              </button>
            </>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 hover:border-red-500 hover:text-red-400"
          >
            <LogOut className="h-3 w-3" />
            Logout
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex w-72 flex-col border-r border-zinc-800 bg-zinc-950">
          <FolderTree
            selectedId={folderId}
            onSelect={(id) => setFolderId(id)}
            onCreate={handleCreateFolder}
          />
          <div className="flex-1 overflow-hidden">
            <NoteList
              selectedId={selectedId}
              folderId={folderId}
              onSelect={(id) => {
                setSelectedId(id);
                setView("editor");
              }}
              onCreate={handleCreate}
            />
          </div>
        </div>

        <main className="flex flex-1 flex-col overflow-hidden">
          {view === "kanban" ? (
            <KanbanView
              onOpenNote={(id) => {
                setSelectedId(id);
                setView("editor");
              }}
            />
          ) : view === "graph" ? (
            <GraphView
              rootId={selectedId ?? undefined}
              onOpenNote={(id) => setPreviewNoteId(id)}
            />
          ) : (
            <>
              {!selectedId && (
                <div className="flex flex-1 items-center justify-center text-zinc-500">
                  Wähle eine Note links — oder lege eine neue an.
                </div>
              )}
              {selectedId && noteQ.data && (
                <>
                  <div className="flex items-center gap-4 border-b border-zinc-800 px-4 py-2">
                    <FolderSelect
                      value={noteQ.data.folder_id}
                      disabled={saveMut.isPending}
                      onChange={(folderId) =>
                        saveMut.mutate({
                          id: noteQ.data!.id,
                          folder_id: folderId,
                          version: noteQ.data!.version,
                        })
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <TagChips
                        tags={noteQ.data.tag_names ?? []}
                        onChange={handleSaveTags}
                        disabled={saveMut.isPending}
                      />
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    {editorMode === "edit" ? (
                      <NoteEditor
                        key={selectedId}
                        initialDoc={noteQ.data.content_md}
                        onSave={handleSaveContent}
                        onNavigateWikilink={handleNavigateWikilink}
                      />
                    ) : (
                      <MarkdownPreview
                        source={noteQ.data.content_md}
                        onWikilinkClick={handleNavigateWikilink}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </main>

        {view === "editor" && selectedId && (
          <aside className="flex h-full w-72 flex-col overflow-y-auto border-l border-zinc-800 bg-zinc-950">
            <AttachmentsPanel noteId={selectedId} />
            <div className="flex-1">
              <BacklinksPanel noteId={selectedId} onOpen={setSelectedId} />
            </div>
          </aside>
        )}
      </div>

      <SearchPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenNote={(id) => {
          setSelectedId(id);
          setView("editor");
        }}
      />

      <ClipDialog
        open={clipOpen}
        onClose={() => setClipOpen(false)}
        onClipped={(id) => {
          setSelectedId(id);
          setView("editor");
          qc.invalidateQueries({ queryKey: ["notes"] });
        }}
      />

      <ApiKeysDialog open={keysOpen} onClose={() => setKeysOpen(false)} />

      <VaultPanel open={vaultOpen} onClose={() => setVaultOpen(false)} />

      {chatOpen && (
        <ChatPanel
          onClose={() => setChatOpen(false)}
          onOpenNote={(id) => {
            setSelectedId(id);
            setView("editor");
            setChatOpen(false);
          }}
        />
      )}

      {previewNoteId && (
        <NotePreviewDialog
          noteId={previewNoteId}
          onClose={() => setPreviewNoteId(null)}
          onOpenFull={(id) => {
            setPreviewNoteId(null);
            setSelectedId(id);
            setView("editor");
          }}
        />
      )}

      {revisionsOpen && selectedId && (
        <RevisionsPanel
          noteId={selectedId}
          onClose={() => setRevisionsOpen(false)}
          onRestore={(content_md, title) => {
            if (!noteQ.data) return;
            saveMut.mutate({
              id: noteQ.data.id,
              content_md,
              title,
              version: noteQ.data.version,
            });
            setRevisionsOpen(false);
          }}
        />
      )}
    </div>
  );
}
